import { VIEW_BREAKPOINTS } from "./breakpoints";

export const FONT_PRIMARY = "\"Manrope\", system-ui, sans-serif";
export const FONT_MONO = "\"JetBrains Mono\", ui-monospace, monospace";

export const TEXT_SCALE = {
  "xs": 10,
  "sm": 12,
  "sm+": 13,
  "base": 14,
  "base+": 15,
  "md": 16,
  "md+": 18,
  "lg": 20,
  "xl": 24,
  "xl+": 28,
  "2xl": 32,
  "2xl+": 36,
  "3xl": 38,
  "3xl+": 40,
  "4xl": 64,
  "5xl": 96
} as const;

export interface FontWeightScale {
  readonly light: number;
  readonly regular: number;
  readonly medium: number;
  readonly semibold: number;
  readonly bold: number;
  readonly extrabold: number;
}

export const FONT_WEIGHT: FontWeightScale = {
  "light": 300,
  "regular": 400,
  "medium": 500,
  "semibold": 600,
  "bold": 700,
  "extrabold": 800
};

export const VIEW_THEME = {
  breakpoints: VIEW_BREAKPOINTS,
  colors: {
  "base": {
    "color-bg": "#0c1c33",
    "color-fg": "#ffffff",
    "surface-base": "#142a4a",
    "surface-raised": "rgba(255, 255, 255, 0.04)",
    "surface-overlay": "rgba(12, 28, 51, 0.78)",
    "accent-blue-15": "rgba(76, 126, 255, 0.15)",
    "border-subtle": "rgba(255, 255, 255, 0.12)",
    "border-strong": "rgba(255, 255, 255, 0.32)",
    "glass-border": "rgba(255, 255, 255, 0.15)",
    "blur-radius": "10px",
    "switch-track": "rgba(76, 126, 255, 0.15)",
    "switch-knob": "#4c7eff",
    "search-bg": "rgba(255, 255, 255, 0.04)",
    "search-border": "transparent",
    "header-bg": "rgba(12, 28, 51, 0.75)",
    "header-border": "rgba(255, 255, 255, 0.15)",
    "sidebar-bg": "linear-gradient(180deg, #0c1c33 0%, #1a4788 100%)",
    "sidebar-border": "rgba(255, 255, 255, 0.32)",
    "accent-blue": "#4c7eff",
    "accent-mint": "#60f6d2",
    "color-on-accent": "#ffffff",
    "surface-accent-subtle": "rgba(76, 126, 255, 0.08)",
    "surface-accent-strong": "rgba(76, 126, 255, 0.12)",
    "surface-mint-subtle": "rgba(96, 246, 210, 0.14)",
    "border-accent-subtle": "rgba(76, 126, 255, 0.15)",
    "overlay-subtle": "rgba(255, 255, 255, 0.04)",
    "overlay-hover": "rgba(255, 255, 255, 0.08)",
    "status-info": "#7eb2ff",
    "status-warning": "#ffc16f",
    "status-positive": "#81c784",
    "status-negative": "#e57373",
    "text-soft": "rgba(255, 255, 255, 0.84)",
    "text-muted": "rgba(255, 255, 255, 0.5)",
    "text-tertiary": "rgba(255, 255, 255, 0.35)",
    "text-faint": "rgba(255, 255, 255, 0.2)"
  },
  "light": {
    "color-bg": "#f0f4f8",
    "color-fg": "#0c1c33",
    "surface-base": "#ffffff",
    "surface-raised": "rgba(12, 28, 51, 0.04)",
    "surface-overlay": "rgba(240, 244, 248, 0.92)",
    "accent-blue-15": "rgba(76, 126, 255, 0.1)",
    "border-subtle": "rgba(12, 28, 51, 0.08)",
    "border-strong": "rgba(12, 28, 51, 0.16)",
    "glass-border": "rgba(12, 28, 51, 0.12)",
    "blur-radius": "8px",
    "switch-track": "rgba(12, 28, 51, 0.18)",
    "switch-knob": "#ffffff",
    "search-bg": "#ffffff",
    "search-border": "rgba(12, 28, 51, 0.15)",
    "header-bg": "rgba(240, 244, 248, 0.85)",
    "header-border": "rgba(12, 28, 51, 0.08)",
    "sidebar-bg": "linear-gradient(180deg, #ffffff 0%, #eef2ff 100%)",
    "sidebar-border": "rgba(12, 28, 51, 0.08)",
    "accent-blue": "#4c7eff",
    "accent-mint": "#10b981",
    "color-on-accent": "#ffffff",
    "surface-accent-subtle": "rgba(76, 126, 255, 0.08)",
    "surface-accent-strong": "rgba(76, 126, 255, 0.12)",
    "surface-mint-subtle": "rgba(16, 185, 129, 0.14)",
    "border-accent-subtle": "rgba(76, 126, 255, 0.18)",
    "overlay-subtle": "rgba(12, 28, 51, 0.05)",
    "overlay-hover": "rgba(12, 28, 51, 0.08)",
    "status-info": "#2563eb",
    "status-warning": "#d97706",
    "status-positive": "#15803d",
    "status-negative": "#dc2626",
    "text-soft": "rgba(12, 28, 51, 0.72)",
    "text-muted": "rgba(12, 28, 51, 0.55)",
    "text-tertiary": "rgba(12, 28, 51, 0.4)",
    "text-faint": "rgba(12, 28, 51, 0.25)"
  }
},
  layout: {
  "spaces": {
    "0": "0",
    "1": "4px",
    "2": "8px",
    "3": "12px",
    "4": "16px",
    "5": "20px",
    "6": "24px",
    "7": "32px",
    "8": "40px",
    "9": "48px",
    "10": "64px"
  },
  "radii": {
    "badge": "3px",
    "button-sm": "4px",
    "social": "11px",
    "input": "12px",
    "identity": "14px",
    "nav": "15px",
    "button": "20px",
    "header": "25px",
    "frame": "24px",
    "sidebar": "32px",
    "card": "35px",
    "pill": "55px",
    "full": "9999px"
  },
  "shadows": {
    "card": "0 0 32px rgba(12, 28, 51, 0.08)",
    "sidebar": "8px 0 64px rgba(12, 28, 51, 0.08)",
    "tooltip": "0 0 35px rgba(0, 0, 0, 0.1)"
  }
},
  motion: {
  "easing": {
    "default": "cubic-bezier(0.4, 0, 0.2, 1)"
  },
  "duration": {
    "fast": "150ms",
    "normal": "250ms",
    "slow": "350ms"
  }
},
  fonts: {
  "families": {
    "primary": "\"Manrope\", system-ui, sans-serif",
    "mono": "\"JetBrains Mono\", ui-monospace, monospace",
    "primaryZh": "\"Huninn\", \"Manrope\", system-ui, sans-serif"
  },
  "textScale": {
    "xs": 10,
    "sm": 12,
    "sm+": 13,
    "base": 14,
    "base+": 15,
    "md": 16,
    "md+": 18,
    "lg": 20,
    "xl": 24,
    "xl+": 28,
    "2xl": 32,
    "2xl+": 36,
    "3xl": 38,
    "3xl+": 40,
    "4xl": 64,
    "5xl": 96
  },
  "fontWeight": {
    "light": 300,
    "regular": 400,
    "medium": 500,
    "semibold": 600,
    "bold": 700,
    "extrabold": 800
  },
  "localeOverrides": [
    {
      "selector": "html[lang=\"zh-CN\"], html[lang^=\"zh-\"]",
      "vars": {
        "font-primary": "\"Huninn\", \"Manrope\", system-ui, sans-serif"
      }
    }
  ]
},
} as const;
